/* Offline-tolerant queue for proof-of-delivery photos.
 *
 * Drivers lose signal in loading docks, lifts and rural drops. A photo that
 * fails to upload must survive that - and survive the app being closed - or
 * the driver has to go back to the address. Blobs are held in IndexedDB
 * (localStorage cannot store binary of this size) and retried whenever the
 * browser regains connectivity or the app is reopened.
 */
const DB_NAME = 'dav-driver';
const STORE = 'pending-photos';
/* v2 added the clock-punch store used by services/timeclock.js. Both modules
 * must ask for the same version and create both stores: whichever one opens
 * the database first runs the upgrade, and a module still asking for v1 would
 * be refused outright - which would mean a driver's held photos becoming
 * unreachable. Adding a store does not touch rows already in this one. */
const DB_VERSION = 2;
const CLOCK_STORE = 'pending-clock';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(CLOCK_STORE)) {
        db.createObjectStore(CLOCK_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

export async function enqueue(item) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').add({ ...item, queuedAt: Date.now(), attempts: 0 });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function all() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readonly').getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function remove(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function bump(id, attempts) {
  const db = await openDb();
  const store = tx(db, 'readwrite');
  return new Promise((resolve) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const row = get.result;
      if (!row) return resolve();
      row.attempts = attempts;
      row.lastTriedAt = Date.now();
      store.put(row);
      resolve();
    };
    get.onerror = () => resolve();
  });
}

export async function count() {
  try {
    return (await all()).length;
  } catch {
    return 0;
  }
}

/** Try to send everything queued. Returns how many uploaded. */
export async function flush(API) {
  let sent = 0;
  let items = [];
  try {
    items = await all();
  } catch {
    return 0;
  }
  for (const item of items) {
    try {
      const form = new FormData();
      form.append('file', item.blob, item.filename || 'photo.jpg');
      form.append('kind', item.kind || 'proof');
      if (item.note) form.append('note', item.note);
      if (item.lat != null) form.append('lat', item.lat);
      if (item.lon != null) form.append('lon', item.lon);
      await API.post(`/driver/stops/${encodeURIComponent(item.orderId)}/photos`, form);
      await remove(item.id);
      sent += 1;
    } catch (err) {
      const status = err?.response?.status;
      // 4xx other than "storage full" means the server will never accept this
      // photo (bad type, stop not on this run) - keeping it would retry
      // forever, so drop it and let the driver retake.
      if (status && status >= 400 && status < 500 && status !== 507 && status !== 401 && status !== 408) {
        await remove(item.id);
      } else {
        await bump(item.id, (item.attempts || 0) + 1);
      }
    }
  }
  return sent;
}
